import {
  DataSourceConfig,
  fetchInstruments,
  getBothSideFilters,
} from '@/modules/marketplace/services/datasource-explorer';
import { formatAnyDate } from '@/utils/helpers/dates';
import { Side } from '@/connect/gen/consts/commonconsts_pb';
import { FilterModel } from 'ag-grid-enterprise';
import { ExportMarketplaceRequestSchema } from '@/connect/gen/modules/apiengine/services/venue/venue_pb';
import { marketplaceService } from '@/connect/services/marketplace';
import { useGridApiExplorerStore } from '@/modules/marketplace/store/grid-api-explorer-store';
import { useSnackbarManager } from '@/modules/snackbar/services/snackbar-manager';
import { errorString } from '@/utils/helpers/rest-response';
import { Ref, ref } from 'vue';
import { create } from '@/connect';

export function useDownloadExplorer(): {
  download: (config: DataSourceConfig, ext: 'xlsx' | 'csv') => Promise<void>;
  status: Ref<'csv' | 'xlsx' | null>;
} {
  const status = ref<'csv' | 'xlsx' | null>(null);

  async function download(config: DataSourceConfig, ext: 'xlsx' | 'csv'): Promise<void> {
    try {
      status.value = ext;
      const side = config.tradeIntention === Side.BORROW ? 'borrow' : 'lend';
      const nowStr = formatAnyDate(new Date(), 'yyyyMMdd-HHmmss');
      const name = `marketplace-orderbook-${side}-${nowStr}.${ext}`;
      const content = await exportOrderbook(
        config,
        useGridApiExplorerStore().getGridApi().getFilterModel(),
        ext
      );
      const blob = new Blob([content], {
        type:
          ext === 'xlsx'
            ? 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            : 'text/csv',
      });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = name;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (e) {
      useSnackbarManager().error(errorString(e as Error));
    } finally {
      status.value = null;
    }
  }

  async function exportOrderbook(
    config: DataSourceConfig,
    filterModel: FilterModel,
    ext: 'xlsx' | 'csv'
  ): Promise<Uint8Array> {
    // get all instruments that match the current filters
    const instrumentData = await fetchInstruments(config, filterModel);
    const cusips = instrumentData.map((instrument) => instrument.instrument!.cusip);
    const request = create(ExportMarketplaceRequestSchema, {
      isExcelSheet: ext === 'xlsx',
      tradeIntention: config.tradeIntention,
      rateType: config.rateType,
      ...getBothSideFilters(filterModel, config),
    });
    request.lendFilter!.instruments = cusips;
    request.borrowFilter!.instruments = cusips;

    // download the orderbook for these instruments
    // const request = new ExportMarketplaceRequest(request);
    const response = await marketplaceService.exportMarketplace(request, config.companyIdFilter);
    if (!response.success) throw new Error('Failed to export the orderbook');

    return response.data.content;
  }

  return {
    download,
    status,
  };
}
